"use client";

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/utils/supabase/client';
import { showSuccess, showError } from '@/utils/toast';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle, AlertTriangle } from 'lucide-react';

const AuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const [status, setStatus] = React.useState<'loading' | 'success' | 'error'>('loading');

  React.useEffect(() => {
    let unsub: (() => void) | null = null;
    let timer: number | null = null;

    const processCallback = async () => {
      const { data: { session } } = await supabase.auth.getSession();

      if (session) {
        setStatus('success');
        showSuccess('Email verified. Redirecting...');
        navigate('/dashboard', { replace: true });
        return;
      }

      const { data } = supabase.auth.onAuthStateChange((event, sess) => {
        if (event === 'SIGNED_IN' && sess) {
          setStatus('success');
          showSuccess('Email verified. Redirecting...');
          navigate('/dashboard', { replace: true });
        }
      });

      unsub = () => data.subscription.unsubscribe();

      // Fallback: if no session arrives, direct to login
      timer = window.setTimeout(() => {
        setStatus('error');
        showError('Verification complete. Please log in to continue.');
        navigate('/login', { replace: true });
      }, 5000);
    };

    processCallback();

    return () => {
      if (unsub) unsub();
      if (timer) window.clearTimeout(timer);
    };
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="max-w-md w-full space-y-4">
        {status === 'loading' && (
          <Alert>
            <AlertDescription className="flex items-center">
              <svg className="animate-spin h-4 w-4 mr-2 text-primary" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
              Verifying your email…
            </AlertDescription>
          </Alert>
        )}
        {status === 'success' && (
          <Alert>
            <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
            <AlertDescription>Email verified. Redirecting to your dashboard…</AlertDescription>
          </Alert>
        )}
        {status === 'error' && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4 mr-2" />
            <AlertDescription>We couldn’t complete sign-in automatically. Please log in to continue.</AlertDescription>
          </Alert>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;